import { Route } from '@angular/router';
import { AuthGuard } from 'app/authentication/auth/guards/auth.guard';
import { AdminGuard } from 'app/authentication/auth/guards/admin.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { InitialDataResolver } from 'app/app.resolvers';
import { ModuleGuard } from 'app/authentication/auth/guards/module.guard';
import {
    NotServicesBoHostMatchGuard,
    ServicesBoHostMatchGuard,
    legacyStoreAdminRedirectCanMatch,
    servicesBoClientsRedirectCanMatch,
    servicesBoRootRedirectCanMatch,
    servicesBoWildcardRedirectCanMatch,
    StoreHostMatchGuard,
    storeHostWildcardRedirectCanMatch,
    tenantAppRootRedirectCanMatch,
    tenantAppWildcardRedirectCanMatch,
} from 'app/modules/services-store/guards/services-host.guard';
import { RedirectComponent } from 'app/shared/redirect/redirect.component';
import { singleModuleSurfaceRedirectGuard } from 'app/shared/entitlement/single-module-surface.guard';
import { petardCmsDocsCanMatch } from 'app/modules/store-bo/cms-documentation.component';

export const appRoutes: Route[] = [
    {
        path: '',
        canMatch: [StoreHostMatchGuard],
        loadChildren: () =>
            import('app/modules/services-store/services-storefront.module').then((m) => m.ServicesStorefrontModule),
    },

    {
        path: '',
        pathMatch: 'full',
        canMatch: [servicesBoRootRedirectCanMatch],
        component: RedirectComponent,
    },

    {
        path: '',
        pathMatch: 'full',
        canMatch: [tenantAppRootRedirectCanMatch],
        component: RedirectComponent,
    },

    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'dashboard' },

    /** Legacy singular path → scraper campaigns module. */
    { path: 'campaign', pathMatch: 'full', redirectTo: 'campaigns' },

    // Auth UI is centralized on the API at /auth/* (edge-proxied). No Angular auth pages.

    {
        path: 'share/:token/:publicId',
        component: LayoutComponent,
        data: { layout: 'empty' },
        children: [
            {
                path: '',
                loadComponent: () =>
                    import('app/modules/dms/share-public/share-public.component').then((m) => m.SharePublicComponent),
            },
        ],
    },

    {
        path: 'support/portal',
        component: LayoutComponent,
        data: { layout: 'empty' },
        children: [
            {
                path: '',
                loadComponent: () =>
                    import('app/modules/support/portal/support-portal-login.component').then(
                        (m) => m.SupportPortalLoginComponent
                    ),
            },
            {
                path: 'tickets/:id',
                loadComponent: () =>
                    import('app/modules/support/portal/support-portal-ticket.component').then(
                        (m) => m.SupportPortalTicketComponent
                    ),
            },
        ],
    },

    {
        path: 'billing/portal/:token',
        component: LayoutComponent,
        data: { layout: 'empty' },
        children: [
            {
                path: '',
                loadComponent: () =>
                    import('app/modules/billing/billing-portal-public.component').then(
                        (m) => m.BillingPortalPublicComponent
                    ),
            },
        ],
    },

    {
        path: 'documentation',
        canMatch: [petardCmsDocsCanMatch],
        component: LayoutComponent,
        data: { layout: 'empty' },
        children: [
            {
                path: '',
                loadComponent: () =>
                    import('app/modules/store-bo/cms-documentation.component').then(
                        (m) => m.StoreCmsDocumentationComponent
                    ),
            },
        ],
    },

    {
        path: 'billing/login',
        component: LayoutComponent,
        data: { layout: 'empty' },
        children: [
            {
                path: '',
                loadComponent: () =>
                    import('app/modules/billing/billing-customer-portal-login.component').then(
                        (m) => m.BillingCustomerPortalLoginComponent
                    ),
            },
        ],
    },

    {
        path: 'dashboard',
        canActivate: [AuthGuard, singleModuleSurfaceRedirectGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: { initialData: InitialDataResolver },
        children: [
            {
                path: '',
                loadChildren: () => import('app/modules/dashboard/dashboard.module').then(m => m.DashboardModule),
            },
            {
                path: 'store',
                canMatch: [ServicesBoHostMatchGuard],
                loadChildren: () =>
                    import('app/modules/services-store/services-store-admin.module').then((m) => m.ServicesStoreAdminModule),
            },
            {
                path: 'store',
                canMatch: [NotServicesBoHostMatchGuard, ModuleGuard],
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                data: { module: 'store' },
                loadChildren: () => import('app/modules/store-bo/store-bo.module').then(m => m.StoreBoModule),
            },
        ],
    },

    {
        path: 'admin',
        canMatch: [AdminGuard],
        canActivate: [AdminGuard],
        canActivateChild: [AdminGuard],
        component: LayoutComponent,
        resolve: { initialData: InitialDataResolver },
        children: [
            {
                path: '',
                loadChildren: () => import('app/modules/admin/tenant-admin.module').then(m => m.TenantAdminModule),
            },
        ]
    },

    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: { initialData: InitialDataResolver },
        children: [
            {
                path: 'profile',
                loadChildren: () => import('app/modules/profile/profile.module').then(m => m.ProfileModule)
            },
            {
                path: 'access-denied',
                loadComponent: () =>
                    import('app/shared/access-denied/access-denied.component').then((m) => m.AccessDeniedComponent),
            },
            {
                path: 'clients',
                canMatch: [servicesBoClientsRedirectCanMatch],
                component: RedirectComponent,
            },
            {
                path: 'store',
                canMatch: [legacyStoreAdminRedirectCanMatch],
                component: RedirectComponent,
            },
            {
                path: 'website',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'website' },
                loadChildren: () => import('app/modules/website/website.module').then(m => m.WebsiteModule)
            },
            {
                path: 'dms',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'dms' },
                loadChildren: () => import('app/modules/dms/dms.module').then(m => m.DmsModule)
            },
            {
                path: 'mailing',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'mailing' },
                loadChildren: () => import('app/modules/mailing/mailing.module').then(m => m.MailingModule)
            },
            {
                path: 'calendar',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'mailing' },
                loadChildren: () => import('app/modules/calendar/calendar.module').then(m => m.CalendarModule)
            },

            {
                path: 'hrm',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'hrm' },
                loadChildren: () => import('app/modules/hrm/hrm.module').then(m => m.HrmModule)
            },
            {
                path: 'accounting',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'accounting' },
                loadChildren: () => import('app/modules/accounting/accounting.module').then(m => m.AccountingModule)
            },
            { path: 'accounts', redirectTo: 'accounting/treasury', pathMatch: 'prefix' },
            {
                path: 'payroll',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'payroll' },
                loadChildren: () => import('app/modules/payroll/payroll.module').then(m => m.PayrollModule)
            },
            {
                path: 'pms',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'pms' },
                loadChildren: () => import('app/modules/pms/pms.module').then(m => m.PmsModule)
            },
            {
                path: 'billing',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'billing' },
                loadChildren: () => import('app/modules/billing/billing.module').then(m => m.BillingModule)
            },
            {
                path: 'pos',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'billing' },
                loadChildren: () => import('app/modules/pos/pos.module').then(m => m.PosModule)
            },
            {
                path: 'iptv',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'iptv' },
                loadChildren: () => import('app/modules/iptv/iptv.module').then(m => m.IptvModule)
            },
            {
                path: 'support',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'support' },
                loadChildren: () => import('app/modules/support/support.module').then(m => m.SupportModule),
            },
            {
                path: 'campaigns',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'campaign' },
                loadChildren: () =>
                    import('app/modules/campaign/campaigns.module').then((m) => m.CampaignsModule),
            },
            {
                path: 'marketing',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'marketing' },
                loadChildren: () => import('app/modules/marketing/marketing.module').then(m => m.MarketingModule)
            },
            {
                path: 'wallet',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'wallet' },
                loadChildren: () => import('app/modules/wallet/wallet.module').then(m => m.WalletModule)
            },
            {
                path: 'wiki',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'wiki' },
                loadChildren: () => import('app/modules/wiki/wiki.module').then(m => m.WikiModule)
            },
            {
                path: 'ai',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'ai' },
                loadChildren: () => import('app/modules/ai/ai.module').then(m => m.AiModule)
            },
            {
                path: 'monitor',
                canActivate: [ModuleGuard],
                canActivateChild: [ModuleGuard],
                canMatch: [ModuleGuard],
                data: { module: 'monitor' },
                loadChildren: () => import('app/modules/monitor/monitor.module').then(m => m.MonitorModule)
            },
        ]
    },

    {
        path: '**',
        canMatch: [storeHostWildcardRedirectCanMatch],
        component: RedirectComponent,
    },
    {
        path: '**',
        canMatch: [servicesBoWildcardRedirectCanMatch],
        component: RedirectComponent,
    },
    {
        path: '**',
        canMatch: [tenantAppWildcardRedirectCanMatch],
        component: RedirectComponent,
    },
];
